/**
 * The people who signed up to the customer's app — AUTH-09.
 *
 * Its own component rather than more of Auth.tsx, because it has its own
 * loading, its own paging and its own dialog, and folding all three into a
 * page that is already about turning a feature on would make both harder
 * to read.
 *
 * Every judgement it makes lives in lib/auth-users.ts, where tests hold it.
 * What is here is layout and the asking.
 */

import { useCallback, useEffect, useState } from 'react'
import { useApi } from '../lib/useApi.js'
import { ApiError, type EndUser } from '../lib/api.js'
import { Dialog, Empty, ErrorBox, Loading } from './ui.js'
import { PermissionButton } from './Permissions.js'
import {
  DISABLED_BADGE, DISABLE_USER_CONSEQUENCES, DISABLE_USER_SAFE, ENABLE_USER_CONSEQUENCES,
  REVOKE_USER_CONSEQUENCES, REVOKE_USER_SAFE, UNVERIFIED_NOTE,
  directoryState, disablePrompt, emptyMessage, revokeUserPrompt, sessionLabel, signInMethod,
  verificationLabel,
} from '../lib/auth-users.js'

const PAGE = 25

export function UserDirectory({ projectId, enabled }: { projectId: string; enabled: boolean }) {
  const api = useApi()
  const [users, setUsers] = useState<EndUser[]>([])
  const [total, setTotal] = useState(0)
  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [search, setSearch] = useState('')
  /** What the list on screen was actually fetched for. */
  const [applied, setApplied] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirm, setConfirm] = useState<EndUser | null>(null)
  /** The account a disable/enable is being confirmed for. */
  const [switching, setSwitching] = useState<EndUser | null>(null)
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async (term: string, next?: string) => {
    setLoading(true)
    setError(null)
    try {
      const page = await api.listAuthUsers(projectId, {
        limit: PAGE, ...(next ? { cursor: next } : {}), ...(term ? { search: term } : {}),
      })
      // Appended when paging, replaced when searching: a "load more" that
      // silently dropped the rows above it would lose somebody's place.
      setUsers((prev) => (next ? [...prev, ...page.users] : page.users))
      setTotal(page.total)
      setCursor(page.nextCursor)
      setApplied(term)
    } catch (err) {
      // 409 is the project answering "auth was never set up here", which is
      // a state this component already renders. Anything else is a fault.
      if (err instanceof ApiError && err.status === 409) {
        setUsers([])
        setTotal(0)
        setCursor(undefined)
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not load your users.')
      }
    } finally {
      setLoading(false)
    }
  }, [api, projectId])

  useEffect(() => {
    if (enabled) void load('')
  }, [enabled, load])

  async function revoke() {
    if (!confirm || busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.revokeAuthUser(projectId, confirm.id)
      setNotice(result.message)
      setConfirm(null)
      await load(applied)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not sign them out.')
    } finally {
      setBusy(false)
    }
  }

  async function setDisabled(user: EndUser) {
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const result = await api.setAuthUserDisabled(projectId, user.id, { disabled: !user.disabled })
      setNotice(result.message)
      setSwitching(null)
      await load(applied)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not change that account.')
    } finally {
      setBusy(false)
    }
  }

  const state = directoryState({ enabled, users, search: applied })

  return (
    <section className="panel" style={{ marginTop: 24 }}>
      <h2>
        Application users{' '}
        {state.kind === 'users' && <span className="pill">{total}</span>}
      </h2>
      <p className="sub">
        Rows in your own database. Signing somebody out here does not touch their account.
      </p>

      {enabled && (
        <form
          style={{ margin: '12px 0' }}
          onSubmit={(e) => { e.preventDefault(); void load(search.trim()) }}
        >
          <label htmlFor="auth-user-search" className="sub" style={{ display: 'block', marginBottom: 6 }}>
            Search by email
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              id="auth-user-search"
              className="input"
              placeholder="Find an email address"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1 }}
            />
            <button className="btn" type="submit" disabled={loading}>Search</button>
            {applied !== '' && (
              <button
                className="btn"
                type="button"
                onClick={() => { setSearch(''); void load('') }}
              >
                Clear
              </button>
            )}
          </div>
        </form>
      )}

      {notice && <p className="sub">{notice}</p>}
      {error && <ErrorBox>{error}</ErrorBox>}

      {loading && users.length === 0 ? <Loading /> : state.kind !== 'users' ? (
        <Empty>{emptyMessage(state)}</Empty>
      ) : (
        <>
          <table className="table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Signs in with</th>
                <th>Sessions</th>
                <th>Joined</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {state.users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.email}
                    {u.disabled && <> <span className="pill">{DISABLED_BADGE}</span></>}
                    <div className="sub small">
                      {u.name ?? '—'} · {verificationLabel(u)}
                    </div>
                  </td>
                  <td>{signInMethod(u.providers)}</td>
                  <td className="mono">{sessionLabel(u.liveSessions)}</td>
                  <td className="sub small">{new Date(u.createdAt).toLocaleDateString()}</td>
                  <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <PermissionButton
                      permission="project:update"
                      className={`btn btn-small${u.disabled ? '' : ' btn-danger'}`}
                      onClick={() => { setNotice(null); setSwitching(u) }}
                    >
                      {u.disabled ? 'Turn on' : 'Disable'}
                    </PermissionButton>{' '}
                    <PermissionButton
                      permission="project:delete"
                      className="btn btn-danger btn-small"
                      disabled={u.liveSessions === 0}
                      onClick={() => { setNotice(null); setConfirm(u) }}
                    >
                      Sign out
                    </PermissionButton>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {state.users.some((u) => !u.emailVerified) && (
            <p className="sub small" style={{ marginTop: 8 }}>{UNVERIFIED_NOTE}</p>
          )}
          {cursor && (
            <button
              className="btn"
              style={{ marginTop: 12 }}
              disabled={loading}
              onClick={() => void load(applied, cursor)}
            >
              {loading ? 'Loading…' : 'Show more'}
            </button>
          )}
        </>
      )}

      <Dialog
        open={switching !== null}
        title={switching ? disablePrompt(switching, switching.disabled) : ''}
        busy={busy}
        onClose={() => setSwitching(null)}
      >
        <ul className="sub" style={{ margin: '0 0 12px 18px' }}>
          {(switching?.disabled ? ENABLE_USER_CONSEQUENCES : DISABLE_USER_CONSEQUENCES)
            .map((line) => <li key={line}>{line}</li>)}
        </ul>
        <p className="sub small">{DISABLE_USER_SAFE}</p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button
          className={`btn${switching?.disabled ? ' btn-primary' : ' btn-danger'}`}
          disabled={busy}
          onClick={() => { if (switching) void setDisabled(switching) }}
        >
          {busy
            ? 'Saving…'
            : switching?.disabled ? 'Turn the account on' : 'Stop them signing in'}
        </button>
      </Dialog>

      <Dialog
        open={confirm !== null}
        title={confirm ? revokeUserPrompt(confirm) : ''}
        busy={busy}
        onClose={() => setConfirm(null)}
      >
        <ul className="sub" style={{ margin: '0 0 12px 18px' }}>
          {REVOKE_USER_CONSEQUENCES.map((line) => <li key={line}>{line}</li>)}
        </ul>
        <p className="sub small">{REVOKE_USER_SAFE}</p>
        {error && <ErrorBox>{error}</ErrorBox>}
        <button className="btn btn-danger" disabled={busy} onClick={() => void revoke()}>
          {busy ? 'Signing out…' : 'Sign them out'}
        </button>
      </Dialog>
    </section>
  )
}
